import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiTruck,
  FiFilter,
  FiHash,
  FiFolder,
  FiLock,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Select,
  MultiSelect,
  Skeletons,
  Table,
} from '../../components';
import TableActions from '../../components/Table/TableActions';
import EntityFilesModal from '../../components/EntityFilesModal';
import VehicleForm from '../../components/VehicleForm';
import '../CompanyManager/style.css';

export default function VehicleManager() {
  const navigate = useNavigate();
  // TODO: Implement actual permissions logic here
  const permissions = usePermission('management');
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Company management
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
  const [selectedVehicleForFiles, setSelectedVehicleForFiles] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  async function loadCompanies() {
     try {
       const { data } = await Api.get('/companies');
       setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  const loadVehicles = useCallback(async () => {
    try {
      setLoading(true);
      let data = [];

      if (companyFilter === 'all') {
        const promises = companies.map(c => 
          Api.get(`/companies/${c.id}/vehicles`)
            .then(response => {
                const vehicles = response.data || [];
                return vehicles.map(v => ({ ...v, company: c }));
            })
            .catch((err) => {
              console.error(`Error loading vehicles for company ${c.id}:`, err);
              return [];
            })
        );
        const results = await Promise.all(promises);
        data = results.flat();
      } else {
        const selectedComp = companies.find(c => String(c.id) === String(companyFilter));
        if (selectedComp) {
            const response = await Api.get(`/companies/${selectedComp.id}/vehicles`);
            data = (response.data || []).map(v => ({ ...v, company: selectedComp }));
        }
      }
      setVehicles(data);
      setHasLoadedOnce(true);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    } finally {
      setLoading(false);
    }
  }, [companyFilter, companies]);

  useEffect(() => {
    if (permissions.canView && companies.length > 0) {
      // Validate if the selected company filter still exists in the loaded companies
      if (companyFilter !== 'all' && !companies.find(c => String(c.id) === String(companyFilter))) {
        setCompanyFilter('all');
      } else {
        loadVehicles();
      }
    }
  }, [companies, companyFilter, loadVehicles, permissions.canView]);



  const companyOptions = useMemo(() => [
    { label: 'TODAS', value: 'all' },
    ...companies.map(c => ({ 
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`, 
      value: String(c.id) 
    }))
  ], [companies]);


  const filteredVehicles = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return vehicles.filter(vehicle => {
      const matchesSearch =
        !term ||
        vehicle.plate?.toLowerCase().includes(term) ||
        vehicle.model?.toLowerCase().includes(term) ||
        vehicle.brand?.toLowerCase().includes(term);

      const companyId = vehicle.company_id || vehicle.company?.id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesCompany;
    });
  }, [vehicles, searchTerm, companyFilterValues]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setCompanyFilter(idsWithoutAll[0]);
    } else {
      setCompanyFilter('all');
    }
  }

  function handleCreate() {
    setSelectedVehicle(null);
    setIsModalOpen(true);
  }

  function handleEdit(vehicle) {
    setSelectedVehicle(vehicle);
    setIsModalOpen(true);
  }

  function handleFiles(vehicle) {
    setSelectedVehicleForFiles(vehicle);
    setIsFilesModalOpen(true);
  }

  function handleDeleteClick(vehicle) {
    setSelectedVehicle(vehicle);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!selectedVehicle) return;

    try {
      const companyId = selectedVehicle.company_id || selectedVehicle.company?.id;
      if (companyId) {
          await Api.delete(`/companies/${companyId}/vehicles/${selectedVehicle.id}`);
          await loadVehicles();
          setIsDeleteModalOpen(false);
          setSelectedVehicle(null);
      }
    } catch (error) {
      console.error('Error deleting vehicle:', error);
    }
  }

  const columns = [
    {
      id: 'plate',
      header: 'Veículo',
      width: '2.5fr',
      cellClassName: 'manager-table-cell-primary',
      render: vehicle => (
        <>
          <div className="manager-table-avatar">
            <FiTruck size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">{vehicle.plate}</span>
            <span className="manager-table-subtitle">{vehicle.model}</span>
          </div>
        </>
      ),
    },
    {
      id: 'brand',
      header: 'Marca',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: vehicle => (
        <span className="manager-table-text">{vehicle.brand || '-'}</span>
      ),
    },
    {
      id: 'year',
      header: 'Ano/Cor',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: vehicle => (
        <div className="manager-table-cell-text">
           <span>{vehicle.year || '-'}</span>
           <span className="manager-table-subtitle">{vehicle.color || '-'}</span>
        </div>
      ),
    },
    {
      id: 'renavam',
      header: 'Renavam',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: vehicle => (
        <span className="manager-table-cnpj">
          <FiHash size={14} />
          {vehicle.renavam || '-'}
        </span>
      ),
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      render: vehicle => {
        const companyName = vehicle.company 
          ? (vehicle.company.corporate_name || vehicle.company.trade_name)
          : 'N/A';
        return <span className="manager-table-text">{companyName}</span>;
      },
    },
    {
      id: 'service_station',
      header: 'Posto',
      width: '1.5fr',
      render: vehicle => (
        <span className="manager-table-text">
          {vehicle.serviceStation?.name || '-'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: vehicle => {
        const isActive = vehicle.active;
        return (
          <div className="manager-tags">
            <span
              className={`manager-tag ${
                isActive ? 'manager-tag-success' : 'manager-tag-danger'
              }`}
            >
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: vehicle => (
        <TableActions>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(vehicle);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleFiles(vehicle);
              }}
            >
              <FiFolder size={16} />
              Arquivos
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(vehicle);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  function VehicleManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && (
          <VehicleManagerSkeletonWrapper />
        )}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiTruck size={22} />
                </div>
                <div>
                  <h1>Veículos</h1>
                  <p>
                    Visualize, filtre e gerencie todos os veículos da empresa selecionada.
                  </p>
                </div>
              </div>
              {permissions.canCreate && (
                <Button onClick={handleCreate} className="manager-header-button">
                  <FiPlus size={18} />
                  Novo Veículo
                </Button>
              )}
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {vehicles.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredVehicles.length} de {vehicles.length} veículos
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <MultiSelect
                  id="company-filter"
                  label="Empresa"
                  options={[
                    { value: 'all', label: 'TODAS' },
                    ...companies.map(c => ({
                      value: String(c.id),
                      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
                    })),
                  ]}
                  value={companyFilterValues}
                  onChange={handleCompanyFilterChange}
                  searchable
                />
                <Input
                  id="search"
                  label="Buscar"
                  placeholder="Buscar por placa, modelo ou marca..."
                  icon={FiSearch}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </Card>

            <div className="manager-content">
              <Table
                items={filteredVehicles}
                columns={columns}
                totalItems={filteredVehicles.length}
                emptyMessage="Nenhum veículo encontrado."
                onRowDoubleClick={permissions.canEdit ? handleEdit : undefined}
              />
            </div>
          </>
        )}
      </div>

      <EntityFilesModal
        isOpen={isFilesModalOpen}
        onClose={() => setIsFilesModalOpen(false)}
        entity={selectedVehicleForFiles}
        entityType="vehicle"
        company={selectedVehicleForFiles?.company || companies.find(c => String(c.id) === String(companyFilter))}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedVehicle ? 'Editar Veículo' : 'Novo Veículo'}
      >
        <VehicleForm
          companyId={companyFilter !== 'all' ? Number(companyFilter) : null}
          companies={companies}
          initialData={selectedVehicle}
          onSuccess={() => {
            setIsModalOpen(false);
            loadVehicles();
          }}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar Exclusão"
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir o veículo{' '}
            <span className="delete-modal-company-name">
              {selectedVehicle?.plate}
            </span>
            ?
          </p>
          <p>Essa ação não poderá ser desfeita.</p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={() => setIsDeleteModalOpen(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
          >
            Excluir
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
